import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { FormContext, useForm } from 'react-hook-form';
import Bluebird from 'bluebird';
import { confirmAlert } from 'react-confirm-alert';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { convertApiDataToComponentFormat } from '../helpers/weeklyHoursConverter';
import NotesTextEditor from './NotesTextEditor';
import WeeklyHoursSelector from './WeeklyHoursSelector';
import { newFoodTypes } from '../models/foodTypes';
import ReceiverNeedsProfile from './ReceiverNeedsProfile';
import ButtonWithLoading from './ButtonWithLoading';
import LargeDeliveriesSelector from './LargeDeliveriesSelector';
import ConfirmationDialog from './ConfirmationDialog';
import errorMessages from '../assets/errorMessages';
import snackbarHelper from '../helpers/snackbarHelper';
import { RECEIVING_HOURS_HASH } from '../pages/Donation/DonationManage/Components/PickupsFormVariant/AllDaysTheSame/AllDaysTheSame';
import { Colors } from '../assets/theme/Colors';

const accordionSx = {
  '&.Mui-expanded': {
    margin: 0,
  },
};

const highlightedSx = {
  color: Colors.errorText,
};

const RECEIVING_HOURS_ACCORDION = 'receiving_hours';
const FOOD_PREFERENCES_ACCORDION = 'food_preferences';
const CAPACITY_ACCORDION = 'capacity';
const POPULATION_SERVED_ACCORDION = 'population_served';

const validationResolver = (data, validationContext) => {
  const errors = {};
  const weeklyHoursError = Object.values(validationContext.weekly_receiving_hours || {})
    .flat()
    .find((hour) => hour.error);

  if (weeklyHoursError) {
    errors.weekly_receiving_hours = { message: errorMessages.DROPOFF_START_TIME_BEFORE_END_TIME.message };
  }

  return {
    values: Object.keys(errors).length ? {} : data,
    errors,
  };
};

const ReceiverOperationsForm = ({ receiverLocation, submitHandler, hash, missingInfo = {} }) => {
  const history = useHistory();
  const receivingHoursRef = useRef();
  const foodDescriptionsEntities = useSelector((state) => state.entities.foodDescriptions);
  const receiverNeedsProfileEntities = useSelector((state) => state.entities.receivers.needsProfile);
  const receiverNeedsProfile = receiverNeedsProfileEntities.byLocationId[receiverLocation.id] || [];
  const foodDescriptions = Object.values(foodDescriptionsEntities.byId);
  const [dropoffNotesValue, setDropoffNotesValue] = useState(receiverLocation?.dropoff_notes || '');
  const [weeklyReceivingHours, setWeeklyReceivingHours] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(hash === RECEIVING_HOURS_HASH ? RECEIVING_HOURS_ACCORDION : false);
  const [needsInitialValues, setNeedsInitialValues] = useState({});
  const [highlightedSections, setHighlightedSections] = useState(missingInfo);

  const categories = foodDescriptions.filter((fd) => newFoodTypes.some((ft) => ft.label === fd.name));
  const hoursInitialValues = convertApiDataToComponentFormat(receiverLocation.weekly_receiving_hours);

  const formMethods = useForm({
    defaultValues: {
      weekly_receiving_hours: hoursInitialValues,
      needs_profile: needsInitialValues,
      dropoff_notes: dropoffNotesValue,
      large_deliveries: JSON.parse(receiverLocation?.large_deliveries) || {},
      served_daily: receiverLocation.served_daily || '',
    },
    validationResolver,
    validationContext: {
      weekly_receiving_hours: weeklyReceivingHours,
    },
  });
  const {
    handleSubmit,
    reset,
    register,
    setValue,
    getValues,
    triggerValidation,
    formState: { dirtyFields },
  } = formMethods;

  useEffect(() => {
    const unblock = history.block((loc) => {
      if (!dirtyFields.size) {
        return true;
      }

      confirmAlert({
        message: 'You have unsaved changes!',
        buttons: [
          {
            label: 'Continue Without Saving',
            color: 'secondary',
            onClick: () => {
              unblock();
              history.push(generatePath(loc.pathname));
            },
          },
          {
            label: 'Save',
            color: 'primary',
            onClick: () => {
              // Reduce values needs_profile and large_deliveris values
              const values = Object.entries(getValues()).reduce((acc, [key, value]) => {
                const [group, subKey] = key.split('.');

                if (subKey !== undefined) {
                  acc[group] = acc[group] || {};
                  acc[group][subKey] = value;
                } else {
                  acc[key] = value;
                }

                return acc;
              }, {});

              return Bluebird.try(() => onSubmit(values)).then(() => {
                unblock();
                history.push(generatePath(loc.pathname));
              });
            },
          },
        ],
        customUI: ({ message, onClose, buttons }) => (
          <ConfirmationDialog buttons={buttons} closeDialog={onClose} message={message} />
        ),
      });

      return false;
    });

    return () => {
      unblock();
    };
  }, [dirtyFields]);

  useEffect(() => {
    // Scroll to the receiving hours and open accordion
    if (hash === RECEIVING_HOURS_HASH && receivingHoursRef?.current) {
      const yOffset = -70;
      const element = receivingHoursRef.current;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [hash]);

  useEffect(() => {
    if (receiverNeedsProfile.length && foodDescriptions.length) {
      const needs = Object.assign(
        {},
        ...categories.map((fd) => {
          const need = receiverNeedsProfile.find((n) => n.food_description_id === fd.id);
          return { [fd.id]: need?.rating || 0 };
        })
      );

      reset({
        weekly_receiving_hours: hoursInitialValues,
        needs_profile: needs,
        dropoff_notes: dropoffNotesValue,
        large_deliveries: JSON.parse(receiverLocation?.large_deliveries) || {},
        served_daily: receiverLocation.served_daily || '',
      });

      setNeedsInitialValues(needs);
    }
  }, [receiverNeedsProfileEntities.inflight, foodDescriptionsEntities.inflight]);

  const registerControlledInputs = () => {
    register({ name: 'dropoff_notes' });
    register({ name: 'weekly_receiving_hours' });
  };

  useEffect(() => {
    registerControlledInputs();
  }, [register]);

  const handleAccordionChange = (accordion) => {
    if (openAccordion !== accordion) {
      return setOpenAccordion(accordion);
    }

    return setOpenAccordion(false);
  };

  const handleNotesChange = (value) => {
    setDropoffNotesValue(value);
    setValue('dropoff_notes', value);
  };

  const onSubmit = (values) => {
    const needs = Object.entries(values.needs_profile)
      .map(([key, value]) => ({
        rating: value,
        food_description_id: Number(key),
      }))
      .filter((value) => value.rating !== undefined);

    const data = {
      ...values,
      dropoff_notes: dropoffNotesValue || '',
      weekly_receiving_hours: weeklyReceivingHours || hoursInitialValues,
      needs_profile: needs,
    };

    const missingInfoArray = Object.values(missingInfo);

    if (missingInfoArray.find((mi) => mi)) {
      setHighlightedSections({
        hours: !Object.values(data.weekly_receiving_hours)?.length > 0,
        needs: !needs.find((need) => need?.rating > 0),
        servedDaily: !(Number(values.served_daily) > 0),
      });
    }

    setIsSubmitting(true);

    return Bluebird.try(() => submitHandler(data))
      .then(() => {
        snackbarHelper.success('Receiving Agency operations was updated successfully!');
        setNeedsInitialValues(values.needs_profile);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => {
        setIsSubmitting(false);
        window.scrollTo(0, 0);
        // reset form with the current values - to clear touched / dirty flags (there is currently no other way to do it)
        // reset form is also unregistering previously custom registered fields with useEffect, so we have to bring
        // these back and then trigger validation to show API errors
        return Bluebird.try(() => reset(getValues(), { isSubmitted: true }))
          .then(() => registerControlledInputs())
          .then(() => triggerValidation());
      });
  };

  return (
    <Paper sx={{ padding: { xs: 1, sm: 2 } }}>
      <FormContext {...formMethods}>
        <form style={{ maxWidth: '700px' }} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box width="100%">
                <Typography variant="subtitle1" gutterBottom color="textSecondary">
                  Dropoff Notes
                </Typography>
                <NotesTextEditor
                  value={dropoffNotesValue}
                  onTextChange={handleNotesChange}
                  styles={{
                    '& .ql-editor': {
                      width: '100%',
                      wordBreak: 'break-word',
                      minHeight: '100px',
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Accordion
                elevation={25}
                sx={accordionSx}
                expanded={openAccordion === RECEIVING_HOURS_ACCORDION}
                onChange={() => handleAccordionChange(RECEIVING_HOURS_ACCORDION)}
              >
                <AccordionSummary
                  sx={[highlightedSections?.hours && highlightedSx]}
                  ref={receivingHoursRef}
                  expandIcon={<ExpandMore />}
                >
                  Receiving Hours
                </AccordionSummary>
                <AccordionDetails>
                  <WeeklyHoursSelector
                    onChange={(hours) => {
                      setWeeklyReceivingHours(hours);
                      setValue('weekly_receiving_hours', hours);
                    }}
                    initialValues={hoursInitialValues}
                    hideTitle
                  />
                </AccordionDetails>
              </Accordion>

              <Divider />

              <Accordion
                elevation={25}
                sx={accordionSx}
                expanded={openAccordion === FOOD_PREFERENCES_ACCORDION}
                onChange={() => handleAccordionChange(FOOD_PREFERENCES_ACCORDION)}
              >
                <AccordionSummary sx={[highlightedSections?.needs && highlightedSx]} expandIcon={<ExpandMore />}>
                  Food Preferences
                </AccordionSummary>
                <AccordionDetails>
                  <ReceiverNeedsProfile categories={categories} initialValues={needsInitialValues} />
                </AccordionDetails>
              </Accordion>

              <Divider />

              <Accordion
                elevation={25}
                sx={accordionSx}
                expanded={openAccordion === CAPACITY_ACCORDION}
                onChange={() => handleAccordionChange(CAPACITY_ACCORDION)}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>Capacity</AccordionSummary>
                <AccordionDetails>
                  <LargeDeliveriesSelector />
                </AccordionDetails>
              </Accordion>

              <Divider />

              <Accordion
                elevation={25}
                sx={accordionSx}
                expanded={openAccordion === POPULATION_SERVED_ACCORDION}
                onChange={() => handleAccordionChange(POPULATION_SERVED_ACCORDION)}
              >
                <AccordionSummary sx={[highlightedSections?.servedDaily && highlightedSx]} expandIcon={<ExpandMore />}>
                  Population Served
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="h6" component="h2">
                          Approximately how many individuals do you serve per day?
                        </Typography>
                      </Box>
                      <Box mb={2}>
                        <Typography variant="body2">
                          Food Rescue US uses this information for impact assessment and to help route donations more
                          efficiently.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        name="served_daily"
                        inputRef={register}
                        type="number"
                        variant="outlined"
                        label="Individuals Served Daily"
                        size="small"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12}>
              <ButtonWithLoading
                disabled={!dirtyFields.size}
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isSubmitting}
              >
                Update
              </ButtonWithLoading>
            </Grid>
          </Grid>
        </form>
      </FormContext>
    </Paper>
  );
};

export default ReceiverOperationsForm;
